import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import Warning from '../../../components/warning';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import DirectApi from '../../../directApi';
import Dropdown from '../../../forecast-app/shared/components/dropdowns/dropdown';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import Util from '../../../forecast-app/shared/util/util';
import UpdatePersonMutation from '../../../mutations/update_person_mutation.modern';
import UpdateViewerMutation from '../../../mutations/update_viewer_mutation.modern';
import {PERMISSION_TYPE} from '../../../Permissions';
import {Company, type Person as PersonType} from '../types';
import {UnlinkButton} from '../UnlinkButton';

type HiBobUser = {
	id: string;
	fullName: string;
};

type DropdownOption = {
	label: string;
	value: string | null;
};

type HiBobIntegrationProps = {
	person: PersonType;
	viewUser: boolean;
	company: Company;
};

function useHibobUsers({skip}: {skip: boolean}) {
	const [users, setUsers] = useState<HiBobUser[]>([]);
	const [loading, setLoading] = useState(false);
	const fetchHiBobUsers = () => {
		if (!skip) {
			setLoading(true);

			DirectApi.Fetch('hibob/employees?onlyFetchUnlinkedEmployees=true').then(res => {
				if (res?.employees) {
					setUsers(res.employees);
					setLoading(false);
				} else {
					setUsers([]);
					setLoading(false);
				}
			});
		}
	};
	useEffect(() => {
		fetchHiBobUsers();
	}, []);

	return {users, loading, retry: fetchHiBobUsers};
}

type SelectUserProps = {
	onSelectUser: (hiBobId: string | null) => void;
	connectionEnabled: boolean;
};

const SelectUser: React.FC<SelectUserProps> = ({onSelectUser, connectionEnabled}) => {
	const {users: hiBobUsers, loading: hiBobLoading, retry} = useHibobUsers({skip: !connectionEnabled});

	const dropdownOptions: DropdownOption[] = useMemo(() => {
		return hiBobUsers
			.map((t: HiBobUser) => ({
				value: t.id,
				label: t.fullName,
			}))
			.sort((a, b) => (a.label.toUpperCase().trim() > b.label.toUpperCase().trim() ? 1 : -1));
	}, [hiBobUsers]);

	return (
		<>
			{`Select a profile from the list below to associate this team member with a HiBob profile.`}
			<div className="common-integration-user-dropdown">
				<Dropdown
					value={null}
					options={dropdownOptions}
					onChange={(option: DropdownOption) => onSelectUser(option?.value || null)}
					hideLabel={true}
					placeholder={`Select HiBob profile`}
					customHeight={30}
					integrationDropdown={true}
					integrationLoading={hiBobLoading}
					integrationRetryToFetch={retry}
				/>
			</div>
		</>
	);
};

function useHibobUser({id, skip}: {id: string | undefined; skip: boolean}) {
	const [user, setUser] = useState<HiBobUser>();
	const [loading, setLoading] = useState(false);
	useEffect(() => {
		if (!skip) {
			setLoading(true);
			DirectApi.Fetch(`hibob/employees/${id}`).then(res => {
				if (res?.employee) {
					setUser(res.employee);
					setLoading(false);
				} else {
					setUser(undefined);
					setLoading(false);
				}
			});
		}
	}, [id, skip]);

	return {user, loading};
}

type LinkedUserProps = {
	id: string;
	name: string;
	onUnlink: () => void;
	connectionEnabled: boolean;
};
const LinkedUser: React.FC<LinkedUserProps> = ({id, name, onUnlink, connectionEnabled}) => {
	const {user: hiBobUser} = useHibobUser({id, skip: !connectionEnabled});

	return (
		<>
			{hiBobUser
				? `${name} is linked to the following HiBob profile: ${hiBobUser.fullName}`
				: `${name} is linked with a HiBob profile.`}
			<div>
				<UnlinkButton onClick={onUnlink} />
			</div>
		</>
	);
};

export const HiBobIntegration: React.FC<HiBobIntegrationProps> = ({person, viewUser, company}) => {
	const {formatMessage} = useIntl();

	const setHiBobUser = (hiBobId: string | null) => {
		const [mutation, data] = viewUser
			? [UpdatePersonMutation, {id: person.id, hiBobId}]
			: [UpdateViewerMutation, {hiBobId}];

		if (hiBobId) {
			Util.CommitMutation(mutation, data, null, true);
		} else {
			showModal({
				type: MODAL_TYPE.GENERIC,
				className: 'default-warning-modal',
				content: <Warning message="Are you sure you want to unlink this user from HiBob?" />,
				buttons: [
					{
						text: formatMessage({id: 'common.cancel'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.WHITE,
					},
					{
						text: formatMessage({id: 'common.unlink'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.RED,
						callback: () => Util.CommitMutation(mutation, data, null, true),
					},
				],
			});
		}
	};

	const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);
	if (!company.hiBobEnabled || person.client || (viewUser && !isAdmin)) {
		return null;
	}

	return (
		<fieldset className="common-integration-settings">
			<h3>HiBob</h3>
			<p className="description">
				{person.hiBobId ? (
					<LinkedUser
						id={person.hiBobId}
						name={person.firstName}
						onUnlink={() => setHiBobUser(null)}
						connectionEnabled={company.hiBobConnectionEnabled}
					/>
				) : (
					<SelectUser onSelectUser={setHiBobUser} connectionEnabled={company.hiBobConnectionEnabled} />
				)}
			</p>
		</fieldset>
	);
};
